import { memo, useCallback, useEffect, useState, useMemo } from 'react';
import Select, { GroupBase } from "react-select";

import { PrincipalModel, apiClient } from '@Models';
import { useDebounce, useMountEffect } from '@Hooks';

import {FormFeedback, ReactSelectStyles} from '@Components';
import { usePaginatedLoading } from './Hooks/PersonPickerHooks';
import { renderCustomLabel } from './Helpers/PersonPickerHelpers';
import {IPickerParams} from "@Components/FormControls/Pickers/PersonPicker/MultiPersonPicker";

type PersonPickerChangeCallback = (value: PrincipalModel | null) => void;
type PersonPickerProps = {
    value: number | null;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    pageSize?: number;
    invalid?: boolean;
    isCreatable?: boolean;
    isClearable?: boolean;
    validationErrors?: string[];
    onChange: PersonPickerChangeCallback;
    onGetCustomRequestParams?: () => Partial<IPickerParams>;
}

export const PersonPicker = memo((props: PersonPickerProps) => {
    const { value, isCreatable, className, disabled, isClearable, placeholder, invalid, validationErrors, onChange, onGetCustomRequestParams } = props;
    const { options, isLoading, loadFirstPage, loadNextPage, setOptions, setPage, setGraphNextLink } = usePaginatedLoading();

    const [inputValue, setInputValue] = useState('');
    const [isMounted, setIsMounted] = useState(false);

    const debouncedInputValue = useDebounce(inputValue);

    const getRequestParams = useCallback((inputValue: string = '') => {
        const params: Partial<IPickerParams> = onGetCustomRequestParams ? onGetCustomRequestParams() : {};
        if (inputValue) params.term = inputValue;
        if (value) params.includePrincipalIds = [value];
        if (isCreatable) params.useGraph = isCreatable;

        return params;
    }, [value, isCreatable, onGetCustomRequestParams]);

    useMountEffect(() => {
        loadFirstPage(getRequestParams());
        setIsMounted(true);
    });

    useEffect(() => {
        if (!isMounted) return;

        loadFirstPage(getRequestParams(debouncedInputValue));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onGetCustomRequestParams, debouncedInputValue]);

    const handleInputChange = (value: string) => {
        setInputValue(value);
    }

    const handleChange = (value: PrincipalModel | null) => {
        if (value && !value?.id) {
            handleCreateOption(value);
            return;
        }

        onChange(value);
    }

    const handleCreateOption = async (value: PrincipalModel) => {
        if (!value.objectId) {
            return;
        }

        const { data } = await apiClient.principalPost({ objectId: value.objectId });
        setOptions((options) => [...options.filter(x => x.objectId !== value.objectId), data]);
        setPage(0);
        setGraphNextLink(undefined);
        onChange(data);
    }

    const selectedValue = useMemo(() => {
        return options.find(option => option.id === value);
    }, [options, value]);

    const visibleOptions = useMemo(() => {
        return options.filter(option => {
            const input = inputValue.toLowerCase();
            const displayName = option.displayName.toLowerCase();
            const email = option.email?.toLowerCase();

            return displayName.includes(input) || email?.includes(input);
        });
    }, [options, inputValue]);

    return (
        <>
            <Select<PrincipalModel>
                className={className}
                classNamePrefix="react-select"
                options={visibleOptions}
                value={selectedValue}
                inputValue={inputValue}
                isSearchable
                isLoading={isLoading}
                isDisabled={disabled}
                isClearable={isClearable}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onMenuScrollToBottom={() => loadNextPage(getRequestParams(inputValue))}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                formatOptionLabel={renderCustomLabel}
                styles={{ ...ReactSelectStyles<PrincipalModel, false, GroupBase<PrincipalModel>>(!!invalid), ...({}) }}
                placeholder={placeholder}
                filterOption={() => true} // does not recognize new options with search open // options are filtered on BE
            />
            {!!validationErrors?.length && (
                <FormFeedback>
                    {validationErrors.join(' ')}
                </FormFeedback>
            )}
        </>
    );

});
